import React from 'react';
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Scatter,
  Rectangle,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend
} from 'recharts';
import { Student, SkillLevel } from '../../types';
import { useTenant } from '../../contexts/TenantContext';
import { autoconcienciaVoluntadDimension } from '../AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from '../HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from '../HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from '../CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from '../LenguajeArtistico';
import { motivacionAprendizajeDimension } from '../Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from '../Encuentronaturaleza';
import { habilidadesSocialesDimension } from '../Habilidadessociales';
import { lenguaInglesaDimension } from '../LenguaInglesa';
import { amorDiosDimension } from '../AmordeDios';
import { pensamientoMatematicosDimension } from '../Pensamientomatematico';
import { filterDimensionsByCourse, shouldShowSkillInPDF, isBeforePreKinder } from '../../utils/dimensionFilters';
import { shouldShowDimensionAndSubdimensions } from '../../utils/dimensionSeparationConfig';

// Importar funciones centralizadas
import {
  calculateExpectedPercentage,
  calculatePreviousYearPercentage,
  deriveAnchoredAge,
  getSixYearRangeForAge,
  createAgeTicksAndLabels,
  chartOffsetForMinAge,
  COURSE_TO_AGE_MAP,
} from '../../utils/levelCalculations';

// Importar funciones de cálculo de calificaciones
import {
  calculateDimensionStatistics,
  isValidSkillLevel,
  isNoAplicaLevelSelected,
  getValidLevelsCount,
  getValidLevelsAchieved,
  calculateProgressByAgeTramos,
  getProgressPercentageByTramos
} from '../../utils/gradeCalculationForWithdrawal';


interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface SubDimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
  subDimensions?: SubDimension[];
}

// Tipo para las dimensiones importadas que pueden tener subDimensions
interface DimensionWithSubs {
  id: number;
  name: string;
  skills: Skill[];
  subDimensions?: SubDimension[];
}

interface StudentDimensionsChartProps {
  student: Student;
  showLegend?: boolean;
  height?: number;
  pdfMode?: boolean; // Modo para exportación PDF con textos más grandes
  showSubdimensions?: boolean; // Mostrar subdimensiones disponibles
  tenantKey?: string | null; // Tenant key para filtrado cuando no hay contexto disponible
  dimensionCategories?: { [key: string]: string[] }; // Agrupación de dimensiones por categorías
  showExpectedLevel?: boolean; // Mostrar nivel esperado
  showPreviousYearLevel?: boolean; // Mostrar nivel año anterior
  chartType?: 'bar' | 'radar'; // Tipo de gráfico: barras o radar
}

const COLORS = {
  achieved: '#4A90E2',
  expected: '#F5A623',
  previousYear: '#6B7280'
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    
    // No mostrar tooltip para categorías o espaciadores
    if (data?.isCategory || data?.isSpacer) {
      return null;
    }
    
    return (
      <div className="bg-white p-2 border shadow-lg rounded">
        <p className="font-bold">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value === null ? 'No evaluado' : `${entry.value.toFixed(2)}%`}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill, payload } = props;
  
  // No renderizar barra para categorías o espaciadores
  if (payload?.isCategory || payload?.isSpacer) {
    return null;
  }
  
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill, payload, pdfMode } = props;
  
  // No renderizar scatter para categorías o espaciadores
  if (payload?.isCategory || payload?.isSpacer) {
    return null;
  }
  
  // Tamaños ajustados para modo PDF
  const width = pdfMode ? 2 : 4;
  const height = pdfMode ? 25 : 40;
  
  return (
    <Rectangle
      x={cx - 1}
      y={cy - height / 2}
      width={width}
      height={height}
      fill={fill}
    />
  );
};

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculatePreviousYearLevel = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

const StudentDimensionsChart: React.FC<StudentDimensionsChartProps> = ({ 
  student, 
  showLegend = true, 
  height = 600,
  pdfMode = false,
  showSubdimensions = true,
  tenantKey: propTenantKey,
  dimensionCategories = {},
  showExpectedLevel = true,
  showPreviousYearLevel = true,
  chartType = 'bar'
}) => {
  // Obtener contexto de tenant (con fallback a prop)
  let tenantKey: string | null = null;
  try {
    const context = useTenant();
    tenantKey = context.tenantKey;
  } catch (error) {
    // Si no hay contexto disponible (ej. en PDF), usar prop
    tenantKey = propTenantKey || null;
  }

  if (!student?.course) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800 text-sm">
          No se encontraron datos del estudiante para generar el gráfico.
        </p>
      </div>
    );
  }
  
  // Edad ancla A (preferimos curso y caemos a edad real)
  const anchoredAge = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

  // Lista de todas las dimensiones con sus IDs y subdimensiones (filtrada por tenant Y curso)
  const getAllDimensionsRaw = () => {
    const baseDimensions = [
      { id: 1, name: "Autoconciencia y voluntad", skills: autoconcienciaVoluntadDimension?.skills, dimension: autoconcienciaVoluntadDimension as DimensionWithSubs },
      { id: 2, name: "Habilidades pensamiento", skills: habilidadesPensamientoDimension?.skills, dimension: habilidadesPensamientoDimension as DimensionWithSubs },
      { id: 3, name: "Habilidades comunicacionales en el lenguaje español", skills: habilidadesComunicacionalesDimension?.skills, dimension: habilidadesComunicacionalesDimension as DimensionWithSubs },
      { id: 4, name: "Corporalidad y Movimiento", skills: corporalidadMovimientoDimension?.skills, dimension: corporalidadMovimientoDimension as DimensionWithSubs },
      { id: 5, name: "Lenguaje Artístico", skills: lenguajesArtisticosDimension?.skills, dimension: lenguajesArtisticosDimension as DimensionWithSubs },
      { id: 6, name: "Motivación y disposición hacia el aprendizaje", skills: motivacionAprendizajeDimension?.skills, dimension: motivacionAprendizajeDimension as DimensionWithSubs },
      { id: 7, name: "Encuentro con la Naturaleza y Sociedad", skills: encuentroNaturalezaDimension?.skills, dimension: encuentroNaturalezaDimension as DimensionWithSubs },
      { id: 8, name: "Habilidades de relación y conciencia social", skills: habilidadesSocialesDimension?.skills, dimension: habilidadesSocialesDimension as DimensionWithSubs },
      { id: 9, name: "Pensamiento matemático", skills: pensamientoMatematicosDimension?.skills, dimension: pensamientoMatematicosDimension as DimensionWithSubs },
      { id: 10, name: "Acercamiento a la lengua inglesa", skills: lenguaInglesaDimension?.skills, dimension: lenguaInglesaDimension as DimensionWithSubs }
    ];

    // Solo agregar "Amor a Dios y sentido Cristiano" para tenant CPV
    if (tenantKey === 'cpv.cl' || tenantKey === 'cpv_cl') {
      baseDimensions.push(
        { id: 11, name: "Amor a Dios y sentido Cristiano", skills: amorDiosDimension?.skills, dimension: amorDiosDimension as DimensionWithSubs }
      );
    }

    // 🎯 APLICAR FILTRADO POR CURSO - Filtrar las skills según el curso del estudiante
    return baseDimensions.map(dim => ({
      ...dim,
      skills: dim.skills?.filter(skill => shouldShowSkillInPDF(skill.id, dim.id, student.course)) || [],
      dimension: {
        ...dim.dimension,
        skills: dim.dimension?.skills?.filter(skill => shouldShowSkillInPDF(skill.id, dim.id, student.course)) || []
      }
    }));
  };

  const allDimensionsRaw = getAllDimensionsRaw();

  // Filtrar dimensiones válidas (que tengan skills y dimension definidos)
  const allDimensions = allDimensionsRaw.filter(dim => 
    dim.skills && Array.isArray(dim.skills) && dim.dimension && dim.skills.length > 0
  );

  // Validación de seguridad: si no hay dimensiones válidas, mostrar mensaje de error
  if (allDimensions.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800 text-sm">
          <strong>⚠️ Error:</strong> No se pudieron cargar las dimensiones. Verifica que todos los componentes de dimensiones estén correctamente importados.
        </p>
      </div>
    );
  }

  // Rango EXACTO de 6 años y elementos del eje X
  const { minAge, maxAge } = getSixYearRangeForAge(anchoredAge);
  const { ticks: ageTicks, labels: ageLabels } = createAgeTicksAndLabels(minAge, maxAge);
  const offsetValue = chartOffsetForMinAge(minAge);
  
  // Función auxiliar para obtener la edad del estudiante basada en su curso
  const getAgeFromCourse = (course: string): number => {
    const courseBase = Object.keys(COURSE_TO_AGE_MAP).find(base =>
      course && course.includes(base)
    );
    return COURSE_TO_AGE_MAP[courseBase as keyof typeof COURSE_TO_AGE_MAP] || 3;
  };

  // Función auxiliar para obtener la edad objetivo para el cálculo (edad actual + 1 año)
  const getTargetAgeForCalculation = (course: string): number => {
    const currentAge = getAgeFromCourse(course);
    return currentAge + 1; // La edad objetivo es siempre la siguiente
  };

  // 🎯 NUEVO: Función para calcular progreso histórico (año anterior)
  const calculateHistoricalProgress = (skillIds: number[], dimensionId: number) => {
    if (!student.previousYearLevels || student.previousYearLevels.length === 0) {
      return calculatePreviousYearPercentage(anchoredAge);
    }

    // Mapear previousYearLevels al formato SkillLevel esperado por el calculador
    const historicalSkillLevels: SkillLevel[] = student.previousYearLevels
      .filter(pl => pl.dimensionId === dimensionId)
      .map(pl => ({
        dimensionId: pl.dimensionId,
        skillId: pl.skillId,
        level: pl.level
      }));

    if (historicalSkillLevels.length === 0) {
      return calculatePreviousYearPercentage(anchoredAge);
    }

    // Obtener el nombre de la dimensión
    const dimensionName = allDimensions.find(dim => dim.id === dimensionId)?.name || `Dimensión ${dimensionId}`;
    
    // Obtener el curso anterior (si es posible estimarlo)
    // Para el cálculo de tramos, necesitamos el curso para saber la edad objetivo del año pasado
    const currentAge = getAgeFromCourse(student.course);
    const previousAge = Math.max(3, currentAge - 1);
    
    // Encontrar un nombre de curso que corresponda a la edad anterior
    const previousCourse = Object.entries(COURSE_TO_AGE_MAP).find(([_, age]) => age === previousAge)?.[0] || "Medio Mayor";

    // Calcular el progreso usando el sistema de tramos pero con datos históricos y curso anterior
    const tramoResult = calculateProgressByAgeTramos(
      dimensionId,
      dimensionName,
      previousCourse,
      historicalSkillLevels,
      skillIds
    );

    const UNITS_PER_TRAMO = 20;
    const BASE_UNITS = 20;
    
    let scaledHistorical = BASE_UNITS;
    tramoResult.tramoDetails.forEach((tramo) => {
      scaledHistorical += tramo.fraction * UNITS_PER_TRAMO;
    });

    const isLenguajeVisual = skillIds.length > 0 && skillIds.every(id => id >= 37 && id <= 46);
    const isEscritura = skillIds.length > 0 && skillIds.every(id => id >= 34 && id <= 76) && !isLenguajeVisual;
    
    if (isEscritura) {
      scaledHistorical += UNITS_PER_TRAMO;
    }

    return scaledHistorical;
  };

  // 🎯 NUEVO SISTEMA: Función auxiliar para calcular progreso usando TRAMOS DE EDAD
  // Esto asegura coherencia entre numerador y denominador
  const calculateSkillsProgress = (skillIds: number[], dimensionId: number) => {
    // Obtener el nombre de la dimensión
    const dimensionName = allDimensions.find(dim => dim.id === dimensionId)?.name || `Dimensión ${dimensionId}`;
    
    // 🎯 Usar el nuevo sistema de cálculo por tramos de edad
    // Pasar skillIds para filtrar solo las habilidades específicas (subdimensiones)
    const tramoResult = calculateProgressByAgeTramos(
      dimensionId,
      dimensionName,
      student.course,
      student.skillLevels,
      skillIds // ← Filtrar solo estas habilidades
    );

    // Calcular el nivel esperado para esta edad (posición en el eje X)
    const expectedPercentage = calculateExpectedPercentage(anchoredAge);
    
    // Calcular el nivel del año anterior (REAL si existe, si no por EDAD)
    const rawPreviousYearValue = calculateHistoricalProgress(skillIds, dimensionId);
    
    // 🎯 NUEVO: Calcular posición en el eje X basándose en tramos completados
    // Cada tramo de edad = 20 unidades en el eje X
    // La base del gráfico empieza en 20 unidades (3 años)
    const UNITS_PER_TRAMO = 20;
    const BASE_UNITS = 20; // 3 años = 20 unidades
    
    // Sumar la fracción de cada tramo para obtener la posición real
    let scaledLogrado = BASE_UNITS;
    tramoResult.tramoDetails.forEach((tramo) => {
      // Cada tramo completo (fracción = 1) suma 20 unidades
      // Cada tramo parcial suma proporcionalmente
      scaledLogrado += tramo.fraction * UNITS_PER_TRAMO;
    });

    // 🎯 AJUSTES ESPECIALES POR DIMENSIÓN
    // Lenguaje Visual (IDs 37-46): NO necesita ajuste, se deja como está
    // Escritura (IDs 34-76, excluyendo Lenguaje Visual): tiene un desfase de 1 año porque sus niveles válidos empiezan más tarde
    // IMPORTANTE: Verificar Lenguaje Visual PRIMERO porque sus IDs están dentro del rango de Escritura
    const isLenguajeVisual = skillIds.length > 0 && skillIds.every(id => id >= 37 && id <= 46);
    const isEscritura = skillIds.length > 0 && skillIds.every(id => id >= 34 && id <= 76) && !isLenguajeVisual;
    
    if (isEscritura) {
      // Para Escritura, agregar 1 año base adicional
      scaledLogrado += UNITS_PER_TRAMO;
    }
    // v2 - logs eliminados
    return {
      // ✅ Aplicar offset consistente y escalar según nivel esperado
      logrado: Math.max(0, scaledLogrado - offsetValue),
      esperado: Math.max(0, expectedPercentage - offsetValue),
      previousYear: Math.max(0, rawPreviousYearValue - offsetValue)
    };
  };

  // 🎯 NUEVO SISTEMA: Función auxiliar para calcular estadísticas de dimensiones usando TRAMOS DE EDAD
  // Esto asegura que el porcentaje sea coherente entre el numerador y denominador
  const calculateDimensionStats = (skillIds: number[], dimensionId: number) => {
    // Obtener el nombre de la dimensión
    const dimensionName = allDimensions.find(dim => dim.id === dimensionId)?.name || `Dimensión ${dimensionId}`;
    
    // 🎯 Usar el nuevo sistema de cálculo por tramos de edad
    // Pasar skillIds para filtrar solo las habilidades específicas (subdimensiones)
    const tramoResult = calculateProgressByAgeTramos(
      dimensionId,
      dimensionName,
      student.course,
      student.skillLevels,
      skillIds // ← Filtrar solo estas habilidades
    );

    // Devolver en el formato esperado por el resto del código
    return {
      skillsCompleted: tramoResult.achievedTotal,
      totalSkills: tramoResult.expectedTotal,
      completeness: tramoResult.percentage,
      // Información adicional del sistema de tramos
      tramoDetails: tramoResult.tramoDetails,
      tramosCompleted: tramoResult.tramosCompleted,
      tramosTarget: tramoResult.tramosTarget,
      maxTramoReached: tramoResult.maxTramoReached
    };
  };

  // Generar datos del gráfico incluyendo agrupaciones por categorías
  const chartData = [];
  
  // Si hay categorías definidas, agrupar por categorías
  if (Object.keys(dimensionCategories).length > 0) {
    Object.entries(dimensionCategories).forEach(([categoryName, dimensionNames]) => {
      // Añadir encabezado de categoría
      chartData.push({
        name: categoryName,
        logrado: null,
        esperado: null,
        isCategory: true,
        isDimension: false
      });
      
      // Añadir dimensiones de esta categoría inmediatamente después del encabezado
      dimensionNames.forEach(dimensionName => {
        const dimension = allDimensions.find(d => d.name === dimensionName);
        if (dimension) {
          // Agregar dimensión principal
          const mainProgress = calculateSkillsProgress(
            dimension.skills.map(s => s.id), 
            dimension.id
          );
          
          // 🎯 NUEVA LÓGICA CONFIGURABLE PARA SEPARACIÓN DE DIMENSIONES
          const separationConfig = shouldShowDimensionAndSubdimensions(dimension.id, student.course);
          
          // Solo agregar dimensión madre si la configuración lo permite
          if (separationConfig.showMother) {
            const dimensionStats = calculateDimensionStats(
              dimension.skills.map(s => s.id), 
              dimension.id
            );
            chartData.push({
              name: `   ${dimension.name}`,
              ...mainProgress,
              isDimension: true,
              isCategory: false,
              dimensionStats
            });
          }
          
          if (showSubdimensions && dimension.dimension?.subDimensions && Array.isArray(dimension.dimension.subDimensions) && dimension.dimension.subDimensions.length > 0) {
            // Filtrar subdimensiones que tienen skills válidas
            const validSubdimensions = dimension.dimension.subDimensions.filter(subDim => {
              if (!subDim || !subDim.skills || !Array.isArray(subDim.skills)) return false;
              const validSkillIds = subDim.skills
                .map(s => s.id)
                .filter(skillId => shouldShowSkillInPDF(skillId, dimension.id, student.course));
              return validSkillIds.length > 0;
            });

            // Solo mostrar subdimensiones si la configuración lo permite Y hay subdimensiones válidas
            if (separationConfig.showChildren && validSubdimensions.length > 0) {
              validSubdimensions.forEach(subDim => {
                const validSkillIds = subDim.skills
                  .map(s => s.id)
                  .filter(skillId => shouldShowSkillInPDF(skillId, dimension.id, student.course));
                
                const subProgress = calculateSkillsProgress(validSkillIds, dimension.id);
                
                // Si no mostramos la madre, las subdimensiones se vuelven dimensiones principales
                const isMainDimension = !separationConfig.showMother;
                const subDimensionStats = calculateDimensionStats(validSkillIds, dimension.id);
                
                chartData.push({
                  name: isMainDimension ? `   ${subDim.name}` : `      • ${subDim.name}`,
                  ...subProgress,
                  isDimension: isMainDimension,
                  isCategory: false,
                  dimensionStats: isMainDimension ? subDimensionStats : undefined
                });
              });
            }
          }
        }
      });
      
      // No añadir espaciadores en modo PDF para maximizar espacio
      if (!pdfMode) {
        const categories = Object.keys(dimensionCategories);
        const isLastCategory = categoryName === categories[categories.length - 1];
        if (!isLastCategory) {
          chartData.push({
            name: "",
            logrado: null,
            esperado: null,
            isSpacer: true,
            isDimension: false,
            isCategory: false
          });
        }
      }
    });
  } else {
    // Fallback: mostrar dimensiones sin agrupación (comportamiento original)
    allDimensions.forEach(dimension => {
      // Agregar dimensión principal
      const mainProgress = calculateSkillsProgress(
        dimension.skills.map(s => s.id), 
        dimension.id
      );
      
      // 🎯 NUEVA LÓGICA CONFIGURABLE PARA SEPARACIÓN DE DIMENSIONES (FALLBACK)
      const separationConfig = shouldShowDimensionAndSubdimensions(dimension.id, student.course);
      
      // Solo agregar dimensión madre si la configuración lo permite
      if (separationConfig.showMother) {
        const dimensionStats = calculateDimensionStats(
          dimension.skills.map(s => s.id), 
          dimension.id
        );
        chartData.push({
          name: dimension.name,
          ...mainProgress,
          isDimension: true,
          dimensionStats
        });
      }
      
      if (!pdfMode && showSubdimensions && dimension.dimension?.subDimensions && Array.isArray(dimension.dimension.subDimensions) && dimension.dimension.subDimensions.length > 0) {
        // Filtrar subdimensiones que tienen skills válidas
        const validSubdimensions = dimension.dimension.subDimensions.filter(subDim => {
          if (!subDim || !subDim.skills || !Array.isArray(subDim.skills)) return false;
          const validSkillIds = subDim.skills
            .map(s => s.id)
            .filter(skillId => shouldShowSkillInPDF(skillId, dimension.id, student.course));
          return validSkillIds.length > 0;
        });

        // Solo mostrar subdimensiones si la configuración lo permite Y hay subdimensiones válidas
        if (separationConfig.showChildren && validSubdimensions.length > 0) {
          validSubdimensions.forEach(subDim => {
            const validSkillIds = subDim.skills
              .map(s => s.id)
              .filter(skillId => shouldShowSkillInPDF(skillId, dimension.id, student.course));
            
            const subProgress = calculateSkillsProgress(validSkillIds, dimension.id);
            
            // Si no mostramos la madre, las subdimensiones se vuelven dimensiones principales
            const isMainDimension = !separationConfig.showMother;
            const subDimensionStats = calculateDimensionStats(validSkillIds, dimension.id);
            
            chartData.push({
              name: isMainDimension ? subDim.name : `  • ${subDim.name}`,
              ...subProgress,
              isDimension: isMainDimension,
              dimensionStats: isMainDimension ? subDimensionStats : undefined
            });
          });
        }
      }
    });
  }

  // Validación adicional: verificar que hay datos para el gráfico
  if (chartData.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800 text-sm">
          <strong>⚠️ Aviso:</strong> No hay datos suficientes para generar el gráfico de dimensiones.
        </p>
      </div>
    );
  }

  const maxDimensionNameLength = Math.max(...chartData.map(d => d.name.length));
  const yAxisWidth = Math.min(400, Math.max(maxDimensionNameLength * 8, 300));

  return (
    <>      
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={chartType === 'radar' ? 700 : Math.max(pdfMode ? height * 0.8 : height, chartData.length * (pdfMode ? 25 : 50))}>
        {chartType === 'radar' ? (
          // Gráfico de Radar
          <RadarChart
            data={chartData.filter(d => !d.isCategory && !d.isSpacer)}
            margin={{ top: 20, right: 80, bottom: 20, left: 80 }}
          >
            <PolarGrid stroke="#E5E7EB" />
            <PolarAngleAxis 
              dataKey="name" 
              tick={{ fontSize: 12, fontWeight: 'bold', fill: '#1A202C' }}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, (maxAge - minAge) * 20 + 20]}
              tick={{ fontSize: 10, fill: '#6B7280' }}
              tickFormatter={(value) => {
                const realValue = value + offsetValue;
                const age = (realValue - 20) / 20 + 2.5;
                return `${Math.floor(age)}`;
              }}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                const realValue = (value as number) + offsetValue;
                const age = (realValue - 20) / 20 + 2.5;
                return [`Edad: ${age.toFixed(1)} años`, name];
              }}
            />
            <Legend />
            <Radar 
              name="Nivel Logrado" 
              dataKey="logrado" 
              stroke={COLORS.achieved} 
              fill={COLORS.achieved} 
              fillOpacity={0.6}
              strokeWidth={2}
            />
            {showExpectedLevel && (
              <Radar 
                name="Nivel Esperado" 
                dataKey="esperado" 
                stroke={COLORS.expected} 
                fill={COLORS.expected} 
                fillOpacity={0.3}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            )}
            {showPreviousYearLevel && (
              <Radar 
                name="Nivel Año Anterior" 
                dataKey="previousYear" 
                stroke={COLORS.previousYear} 
                fill={COLORS.previousYear} 
                fillOpacity={0.2}
                strokeWidth={2}
                strokeDasharray="3 3"
              />
            )}
          </RadarChart>
        ) : (
          // Gráfico de Barras (original)
        <ComposedChart
          layout="vertical"
          data={chartData}
            margin={{ top: pdfMode ? 10 : 20, right: 40, left: 20, bottom: pdfMode ? 10 : 20 }}
        >
          <XAxis
            type="number"
            domain={[0, (maxAge - minAge) * 20 + 20]}
            ticks={ageTicks}
            tickFormatter={(value) => {
              const label = ageLabels[value] || "";
              if (pdfMode) {
                return label; // En PDF: "X años"
              } else {
                return label ? `Hacia ${label}` : ""; // En visualización normal: "Hacia X años"
              }
            }}
            tick={{ fontSize: pdfMode ? 17 : 11, fontWeight: 'bold', fill: '#4B5563' }}
            axisLine={{ stroke: '#E5E7EB' }}
            tickLine={{ stroke: '#E5E7EB' }}
            interval={0}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
              width={Math.min(pdfMode ? 400 : 350, yAxisWidth)}
              tick={(props) => {
                const { x, y, payload } = props;
                const data = chartData.find(d => d.name === payload.value);
                
                // No renderizar tick para elementos vacíos (espaciadores)
                if (data?.isSpacer) {
                  return null;
                }
                
                // Estilos para categorías
                if (data?.isCategory) {
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {/* Fondo de la categoría */}
                      <rect
                        x={-Math.min(pdfMode ? 390 : 340, yAxisWidth)}
                        y={-12}
                        width={Math.min(pdfMode ? 390 : 340, yAxisWidth)}
                        height={24}
                        fill="#4A90E2"
                        rx={4}
                      />
                      {/* Texto de la categoría */}
                      <text
                        x={-10}
                        y={4}
                        textAnchor="end"
                        fill="white"
                      fontSize={pdfMode ? 16 : 14}
                      fontWeight="bold"
                      >
                        {payload.value}
                      </text>
                    </g>
                  );
                }
                
                // Estilos para dimensiones y subdimensiones (normal)
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={-10}
                      y={4}
                      textAnchor="end"
                      fill={data?.isDimension ? '#1F2937' : '#6B7280'}
                      fontSize={pdfMode ? 14 : 12}
                      fontWeight={data?.isDimension ? 'bold' : 'medium'}
                    >
                      {payload.value}
                    </text>
                    {/* Agregar métricas de habilidades evaluadas y completitud para dimensiones */}
                    {data?.isDimension && data?.dimensionStats && (
                      <text
                        x={-10}
                        y={18}
                        textAnchor="end"
                        fill="#6B7280"
                        fontSize={pdfMode ? 12 : 10}
                        fontWeight="normal"
                      >
                        {data.dimensionStats.skillsCompleted}/{data.dimensionStats.totalSkills} niveles válidos hasta {getTargetAgeForCalculation(student.course)} años ({data.dimensionStats.completeness.toFixed(1)}% completado)
                      </text>
                    )}
                  </g>
                );
              }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={{ stroke: '#E5E7EB' }}
          />
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          {/* <Tooltip content={<CustomTooltip />} /> */}
          <Bar 
            dataKey="logrado" 
            fill={COLORS.achieved}
            name="Nivel habilidad logrado" 
              barSize={pdfMode ? 20 : 30}
              radius={[3, 3, 3, 3]}
            shape={<CustomBar />}
          />
          {showExpectedLevel && (
            <Bar
              dataKey="esperado"
              fill={COLORS.expected}
              name="Nivel esperado"
                barSize={pdfMode ? 3 : 6}
              shape={<CustomBar />}
            />
          )}
          {showPreviousYearLevel && (
            <Scatter
              dataKey="previousYear"
              fill={COLORS.previousYear}
              name="Nivel año anterior"
              shape={<CustomVerticalRectangle pdfMode={pdfMode} />}
            />
          )}
        </ComposedChart>
        )}
        </ResponsiveContainer>
        
        {/* Leyenda compacta como footer */}
        {showLegend && (
          <div className={`${pdfMode ? 'mt-1 border-t border-gray-300 pt-1' : 'mt-2 border-t border-gray-200 pt-2'}`}>
            <div className={`${pdfMode ? 'space-y-1' : 'space-y-2'}`}>
              
              {/* Nivel Logrado */}
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-1">
                  <div className={`bg-blue-500 rounded ${pdfMode ? 'w-4 h-4' : 'w-3 h-3'}`}></div>
                </div>
                <div className="flex-1">
                  <p className={`text-gray-700 ${pdfMode ? 'text-xs leading-tight' : 'text-xs leading-relaxed'}`}>
                    <strong>Nivel Logrado (Barras Azules):</strong> Representa el nivel actual de desarrollo que ha alcanzado el estudiante en cada dimensión hasta su edad actual. Este nivel se evalúa mediante observaciones sistemáticas del desempeño del niño o niña en situaciones reales de aprendizaje y desarrollo.
                  </p>
                </div>
              </div>

              {/* Nivel Esperado */}
              {showExpectedLevel && (
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`bg-yellow-500 rounded ${pdfMode ? 'w-4 h-1' : 'w-3 h-0.5'}`}></div>
                  </div>
                  <div className="flex-1">
                    <p className={`text-gray-700 ${pdfMode ? 'text-xs leading-tight' : 'text-xs leading-relaxed'}`}>
                      <strong>Nivel Esperado (Líneas Amarillas):</strong> Indica el nivel de desarrollo esperado para la edad y curso del estudiante, basado en estándares pedagógicos reconocidos. Sirve como referencia para evaluar el progreso del niño o niña.
                    </p>
                  </div>
                </div>
              )}

              {/* Nivel Año Anterior */}
              {showPreviousYearLevel && (
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-1">
                    <div className={`bg-gray-500 rounded ${pdfMode ? 'w-1 h-4' : 'w-1 h-3'}`}></div>
                  </div>
                  <div className="flex-1">
                    <p className={`text-gray-700 ${pdfMode ? 'text-xs leading-tight' : 'text-xs leading-relaxed'}`}>
                      <strong>Nivel Año Anterior (Líneas Grises):</strong> Representa el nivel de desarrollo alcanzado durante el año anterior. Sirve como referencia histórica para comparar el progreso del estudiante entre períodos.
                    </p>
                  </div>
                </div>
              )}
              
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default StudentDimensionsChart;